/**
 * The alert taxonomy, as one definition instead of bare ints and string
 * literals spread across the feed and the detail panel.
 *
 * WHERE THESE NUMBERS COME FROM. They are NOT firmware-originated and do not
 * match anything the device sends. Three unrelated numbering systems exist end
 * to end:
 *
 *   1. firmware/gateway MessageType wire opcodes (PUSH_BUTTON = 0x07,
 *      GEO_FENCE_ALERT = 0x09, LSA = 0x0D, TSA = 0x0E, SPEED_ALERT = 0x13)
 *   2. firmware LocationTriggerSource 0-23 (PUSH_BUTTON_TRIGGER = 8,
 *      LOW_BATTERY = 12, LIGHT_SENSOR_EVENT = 13, BATTERY_REMOVED = 14,
 *      TEMP_ALERT = 17, CRASH_ALERT = 23)
 *   3. the API's AssetAlertTypeEnum 1-13, which is what this mirrors
 *
 * The 1-13 value is minted server-side in ConfigController::monitoringAlert
 * from a bare string the gateway posts, so trigger 8 becomes type 4, trigger 12
 * becomes 7 and trigger 14 becomes 13. Do not try to align these with a
 * firmware constant; they are a different vocabulary.
 *
 * Keep in lockstep with App\Enums\AssetAlertTypeEnum (API) and the Go app's
 * lib/models/alert_type.dart.
 */

export const ALERT_TYPE = Object.freeze({
    GEOFENCE: 1,
    SPEED: 2,
    LIGHT: 3,
    PUSH_BUTTON: 4,
    TEMPERATURE: 5,
    MAINTENANCE: 6,
    BATTERY: 7,
    CRASH: 8,
    NO_SHOW: 9,
    PLACE: 10,
    DEPARTURE: 11,
    ARRIVAL: 12,
    SOS: 13,
});

const ALERT_TYPE_VALUES = new Set(Object.values(ALERT_TYPE));

/**
 * The `system_alerts` discriminator. The API maps these onto alert types in
 * ConfigController::monitoringAlert:
 *
 *   battery_power_depleted | charger_connected | charger_disconnected -> 7
 *   crash_detention                                                   -> 8
 *   device_unplugged | push_button_sos                                 -> 13 (SOS)
 *   maintenance_required                                              -> 6
 *
 * Type 7 carrying three sub_types and type 13 carrying BOTH a person's panic
 * SOS and a hardware tamper is why the bare int is not a display taxonomy:
 * rendering on alert_type alone put a red "SOS" chip on an unplugged tracker
 * and an amber "Low Battery" warning on a connected charger.
 */
export const SYSTEM_ALERT_SUB_TYPE = Object.freeze({
    BATTERY_POWER_DEPLETED: 'battery_power_depleted',
    CHARGER_CONNECTED: 'charger_connected',
    CHARGER_DISCONNECTED: 'charger_disconnected',
    CRASH_DETENTION: 'crash_detention',
    DEVICE_UNPLUGGED: 'device_unplugged',
    PUSH_BUTTON_SOS: 'push_button_sos',
    MAINTENANCE_REQUIRED: 'maintenance_required',
});

/** The `type` value the API uses for the whole device-health family. */
export const SYSTEM_ALERTS_TYPE = 'system_alerts';

/** The three sub_types that ride on ALERT_TYPE.BATTERY. */
export const BATTERY_SUB_TYPES = Object.freeze([
    SYSTEM_ALERT_SUB_TYPE.BATTERY_POWER_DEPLETED,
    SYSTEM_ALERT_SUB_TYPE.CHARGER_CONNECTED,
    SYSTEM_ALERT_SUB_TYPE.CHARGER_DISCONNECTED,
]);

/**
 * Numeric alert type for a raw row, or null when it carries none / an unknown
 * one. Tolerant by design: a type the server adds later must degrade to the
 * generic rendering, not break the feed. Accepts the string form the API and
 * FCM both send.
 */
export function alertTypeOf(alert) {
    const raw = alert?.alert_type ?? alert?.type;
    const n = typeof raw === 'number' ? raw : parseInt(raw, 10);
    return Number.isFinite(n) && ALERT_TYPE_VALUES.has(n) ? n : null;
}

/**
 * The system_alerts sub_type for a row, or null.
 *
 * Two row shapes reach this and they nest it differently: a REST row from
 * AssetAlertNotificationResource keeps sub_type inside the `payload` map (the
 * server writes the whole event blob into that column), while a flat push
 * payload carries it at the top level. Read the payload first and fall back to
 * the top level so every surface resolves it identically.
 */
export function subTypeOf(alert) {
    const raw = payloadOf(alert)?.sub_type ?? alert?.sub_type;
    const s = (raw ?? '').toString().trim();
    return s === '' ? null : s;
}

/**
 * The row's `payload` as an object, or null. Some rows deliver it as a JSON
 * string rather than a map, so parse that form (and swallow a malformed one)
 * instead of letting every `payload?.x` read silently come back undefined.
 */
export function payloadOf(alert) {
    const p = alert?.payload;
    if (p == null) return null;
    if (typeof p === 'string') {
        try {
            const parsed = JSON.parse(p);
            return parsed && typeof parsed === 'object' ? parsed : null;
        } catch {
            return null;
        }
    }
    return typeof p === 'object' ? p : null;
}

/** True when the backend filed this under the `system_alerts` family. */
export function isSystemAlert(alert) {
    const t = payloadOf(alert)?.type ?? alert?.type;
    return (t ?? '').toString().trim() === SYSTEM_ALERTS_TYPE;
}

const toCoord = (v) => {
    if (v === null || v === undefined) return null;
    if (typeof v === 'string' && v.trim() === '') return null;
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? n : null;
};

/**
 * {lat, lng} for a PAYLOAD object (not a row), or null. Hardware alerts carry
 * latitude/longitude; a phone SOS carries only lat/lng, so fall back to those.
 * An empty string counts as absent, and 0,0 (no GPS fix) is treated as no
 * location rather than a pin off the coast of Africa.
 */
export function coordsOf(payload) {
    if (!payload || typeof payload !== 'object') return null;
    const lat = toCoord(payload.latitude) ?? toCoord(payload.lat);
    const lng = toCoord(payload.longitude) ?? toCoord(payload.lng);
    if (lat === null || lng === null) return null;
    if (lat === 0 && lng === 0) return null;
    return {lat, lng};
}

/**
 * True when a payload says which way the subject crossed. enter_or_exit is
 * 1 for arrived/entered and ANY other value (0, 2, ...) for left, so only a
 * missing, null or empty key means "no direction".
 */
export function hasDirection(payload) {
    const v = payload?.enter_or_exit;
    return v !== null && v !== undefined && String(v).trim() !== '';
}

/** enter_or_exit 1 = arrived/entered; anything else = left. */
export function isEntered(payload) {
    return hasDirection(payload) && Number(payload.enter_or_exit) === 1;
}

/** True when the alert is a hardware tamper rather than a person's SOS. */
export function isTamper(alert) {
    return subTypeOf(alert) === SYSTEM_ALERT_SUB_TYPE.DEVICE_UNPLUGGED;
}

/**
 * True when a person pressed the panic button ON THE TRACKER (firmware trigger
 * 8). Files as type 13 like the app's own panic SOS and the hardware tamper, but
 * is neither: there is no initiator user to call back, yet it IS a deliberate
 * human cry for help rather than a power event.
 */
export function isPushButton(alert) {
    return subTypeOf(alert) === SYSTEM_ALERT_SUB_TYPE.PUSH_BUTTON_SOS;
}

/** Device-raised SOS (tamper or panic button); neither has a human initiator. */
export function isDeviceSos(alert) {
    return isTamper(alert) || isPushButton(alert);
}

/** True for a charger connect/disconnect, which is NOT a low-battery warning. */
export function isChargerEvent(alert) {
    const sub = subTypeOf(alert);
    return sub === SYSTEM_ALERT_SUB_TYPE.CHARGER_CONNECTED
        || sub === SYSTEM_ALERT_SUB_TYPE.CHARGER_DISCONNECTED;
}

/** Crash and SOS carry a heightened (red/urgent) accent. */
export function isUrgentType(type) {
    return type === ALERT_TYPE.CRASH || type === ALERT_TYPE.SOS;
}

/**
 * True for a person's emergency: the app's own SOS (type 13 with no
 * sub_type) or the tracker's panic button. Excludes the hardware tamper,
 * which files as 13 too but is a "possible tampering" notice, not a cry for
 * help, and must not get the red SOS chip or a 911 button.
 */
export function isEmergencySos(alert) {
    return resolveType(alert) === ALERT_TYPE.SOS && !isTamper(alert);
}

// --- Display face (label / icon / colour) ---------------------------------
//
// The API's `alert_type_name` is a generic bucket ("GENERAL ALERT" for 6/7/8,
// "SOS ALERT" for 13) and must never be displayed. Every surface resolves the
// face here instead, keyed on type first and refined by sub_type, so the feed
// and the detail panel cannot drift apart again.

/** Vuetify palette hex values, so they work as both a `color` prop and CSS. */
const C = Object.freeze({
    RED: '#F44336',
    ORANGE: '#FF9800',
    DEEP_ORANGE: '#FF5722',
    AMBER: '#FFC107',
    PURPLE: '#9C27B0',
    BLUE: '#2196F3',
    PINK: '#E91E63',
    GREEN: '#4CAF50',
    INDIGO: '#3F51B5',
    TEAL: '#009688',
    DEFAULT: '#1976D2',
});

const TYPE_FACE = Object.freeze({
    [ALERT_TYPE.GEOFENCE]: {label: 'Geofence Alert', icon: 'mdi-map-marker-radius', color: C.ORANGE},
    [ALERT_TYPE.SPEED]: {label: 'Speed Alert', icon: 'mdi-speedometer', color: C.RED},
    [ALERT_TYPE.LIGHT]: {label: 'Light Sensor Alert', icon: 'mdi-lightbulb-on', color: C.AMBER},
    [ALERT_TYPE.PUSH_BUTTON]: {label: 'Push Button Alert', icon: 'mdi-gesture-tap-button', color: C.PURPLE},
    [ALERT_TYPE.TEMPERATURE]: {label: 'Temperature Alert', icon: 'mdi-thermometer', color: C.BLUE},
    [ALERT_TYPE.MAINTENANCE]: {label: 'Maintenance / Check Engine', icon: 'mdi-engine', color: C.ORANGE},
    [ALERT_TYPE.BATTERY]: {label: 'Battery Alert', icon: 'mdi-battery-alert', color: C.RED},
    [ALERT_TYPE.CRASH]: {label: 'Crash Detected', icon: 'mdi-car-emergency', color: C.RED},
    [ALERT_TYPE.NO_SHOW]: {label: 'No-Show Alert', icon: 'mdi-clock-alert-outline', color: C.PINK},
    [ALERT_TYPE.PLACE]: {label: 'Place Alert', icon: 'mdi-map-marker-check', color: C.GREEN},
    [ALERT_TYPE.DEPARTURE]: {label: 'Departure Alert', icon: 'mdi-logout-variant', color: C.INDIGO},
    [ALERT_TYPE.ARRIVAL]: {label: 'Arrival Alert', icon: 'mdi-login-variant', color: C.TEAL},
    [ALERT_TYPE.SOS]: {label: 'SOS Emergency', icon: 'mdi-alarm-light', color: C.RED},
});

const SUB_TYPE_FACE = Object.freeze({
    [SYSTEM_ALERT_SUB_TYPE.BATTERY_POWER_DEPLETED]: {label: 'Low Battery', icon: 'mdi-battery-alert', color: C.RED},
    [SYSTEM_ALERT_SUB_TYPE.CHARGER_CONNECTED]: {label: 'Charger Connected', icon: 'mdi-power-plug', color: C.GREEN},
    [SYSTEM_ALERT_SUB_TYPE.CHARGER_DISCONNECTED]: {label: 'Charger Disconnected', icon: 'mdi-power-plug-off-outline', color: C.ORANGE},
    [SYSTEM_ALERT_SUB_TYPE.CRASH_DETENTION]: {label: 'Crash Detected', icon: 'mdi-car-emergency', color: C.RED},
    [SYSTEM_ALERT_SUB_TYPE.MAINTENANCE_REQUIRED]: {label: 'Maintenance / Check Engine', icon: 'mdi-engine', color: C.ORANGE},
    // Tamper is NOT an emergency: deep orange, plug-off icon, no SOS wording.
    [SYSTEM_ALERT_SUB_TYPE.DEVICE_UNPLUGGED]: {label: 'Possible Tampering', icon: 'mdi-power-plug-off', color: C.DEEP_ORANGE},
    // A panic press on the tracker IS an emergency: SOS red, alarm icon.
    [SYSTEM_ALERT_SUB_TYPE.PUSH_BUTTON_SOS]: {label: 'Panic Button', icon: 'mdi-alarm-light', color: C.RED},
});

const GENERIC_FACE = Object.freeze({label: 'Alert', icon: 'mdi-bell-alert', color: C.DEFAULT});

/**
 * Numeric type for a row, also reading a type that only rides inside the
 * payload (the detail panel is sometimes handed a row whose top level lacks it).
 */
function resolveType(alert) {
    return alertTypeOf(alert) ?? alertTypeOf(payloadOf(alert));
}

/** {label, icon, color} for a row (REST row, flat push payload, or payload). */
export function alertFace(alert) {
    const sub = subTypeOf(alert);
    if (sub && SUB_TYPE_FACE[sub]) return SUB_TYPE_FACE[sub];
    const t = resolveType(alert);
    if (t !== null && TYPE_FACE[t]) return TYPE_FACE[t];
    // Unknown type on a system_alerts row: the API's default arm is maintenance.
    if (isSystemAlert(alert)) return TYPE_FACE[ALERT_TYPE.MAINTENANCE];
    return GENERIC_FACE;
}

export const alertLabel = (alert) => alertFace(alert).label;
export const alertIcon = (alert) => alertFace(alert).icon;
export const alertColor = (alert) => alertFace(alert).color;

/** Human label for a bare sub_type string; null when there is none. */
export function subTypeLabel(sub) {
    const s = (sub ?? '').toString().trim();
    if (s === '') return null;
    return SUB_TYPE_FACE[s]?.label
        ?? s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
